import React, { useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, Link as RouterLink } from 'react-router-dom';
import { Button, Chip, Icon } from '@material-ui/core';
// Components
import App from '../../../components/app';
import ButtonTo from '../../../components/buttonTo';
// Modules
import { useDispatch, useSelector } from 'react-redux';
import { actions as rankActions, selectors as rankSelectors } from '../../../redux/reducers/rank';
import { selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations/module';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import { paths } from '../../paths';
import { DispatchGroup, HourRange, Rank, TimeSlots, WAITING_LINE_STATUS } from '../../../redux/reducers/api-types';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import Listing from '../../../components/listing';
import { TooltipButton } from '../../../components/tooltip-button';
import ErrorMessage from '../../../components/error-message';
import DateUtils from '../../../utils/dateUtils';
import { clsx } from '../../../utils/htmlUtils';
import useStyles from './ranks-list-style';

const EMPTY_VALUE = '—';

// Days sharing the same hours are merged as long as they follow each other
interface OpeningHoursGroup {
    days: string[];
    hours: HourRange[];
}

/**
 * Turn the weekly opening hours into the fewest readable lines: "Lu-Ve 08:00-20:00"
 * rather than the same range repeated five times.
 */
const groupOpeningHours = (openingHours?: TimeSlots): OpeningHoursGroup[] => {
    const groups: Array<OpeningHoursGroup & { signature: string }> = [];

    DateUtils.sortingDaysArr.forEach((day) => {
        const hours: HourRange[] = openingHours?.[day];
        if (!hours?.length) {
            return;
        }
        const signature = hours.map((hour) => `${hour.start}-${hour.end}`).join(' ');
        const previous = groups[groups.length - 1];
        if (previous?.signature === signature) {
            previous.days.push(day);
        } else {
            groups.push({ signature, days: [day], hours });
        }
    });

    return groups;
};

/**
 * The waiting line references its dispatch groups by id, stored in the `name` field of the
 * queue (the edit form matches them the same way). Fall back to the raw value when the
 * dispatch group list has no entry for it.
 */
const getDispatchGroupNames = (rank: Rank, dispatchGroups: DispatchGroup[]): string[] =>
    (rank.dg ?? []).map(
        (queue) =>
            dispatchGroups.find((group) => group.id === queue.name || group.id === queue.id)?.name ??
            queue.name ??
            queue.id,
    );

/**
 * Anything that is not active, ie inactive *and* closed waiting lines. A rank without
 * status stays visible: nothing tells us it is out of service.
 */
const isInactive = (rank: Rank): boolean => !!rank.status && rank.status !== WAITING_LINE_STATUS.ACTIVE;

const getStatusClass = (status: WAITING_LINE_STATUS, classes: Record<string, string>): string => {
    if (status === WAITING_LINE_STATUS.ACTIVE) {
        return classes.chipActive;
    }
    return status === WAITING_LINE_STATUS.CLOSED ? classes.chipClosed : classes.chipInactive;
};

interface RanksListPageProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
}

const RanksListPage = ({ t, globalClasses }: RanksListPageProps): JSX.Element => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { ranks, loadingRanks } = useSelector((state: any): { ranks: Rank[]; loadingRanks: boolean } => ({
        ranks: rankSelectors.ranks(state) as Rank[],
        loadingRanks: pendingSelectors.isLoadingRanks(state),
    }));
    // Needed to show the dispatch group names, the waiting lines only carry their ids
    const storedDispatchGroups = useSelector(rankSelectors.dispatchGroups) as DispatchGroup[];
    const dispatchGroups = Array.isArray(storedDispatchGroups) ? storedDispatchGroups : [];
    const rankError = useSelector((state: any) => errorSelectors.getRankError(state));

    useEffect(() => {
        dispatch(rankActions.getRanks());
        dispatch(rankActions.getDispatchGroup());
    }, [dispatch]);

    const [showInactive, setShowInactive] = React.useState<boolean>(false);

    const sortedRanks = React.useMemo(
        () => (Array.isArray(ranks) ? [...ranks].sort((a, b) => a.name.localeCompare(b.name)) : []),
        [ranks],
    );

    const inactiveCount = sortedRanks.filter(isInactive).length;
    const visibleRanks = showInactive ? sortedRanks : sortedRanks.filter((rank) => !isInactive(rank));

    //=======================================//
    //=============== Renders ===============//
    //=======================================//

    const renderDetail = (label: string, value: string): JSX.Element => (
        <div className={classes.detail}>
            <div className={classes.detailLabel}>{label}</div>
            <div className={clsx(classes.detailValue, value === EMPTY_VALUE && classes.detailEmpty)}>{value}</div>
        </div>
    );

    const renderOpeningHours = (openingHours?: TimeSlots): string => {
        const groups = groupOpeningHours(openingHours);
        if (groups.length === 0) {
            return EMPTY_VALUE;
        }

        return groups
            .map(({ days, hours }) => {
                const dayLabel = (day: string): string => t(`common:days_short.${day}`);
                // Three days or more read better as a range than as an enumeration
                const daysLabel =
                    days.length > 2
                        ? `${dayLabel(days[0])}-${dayLabel(days[days.length - 1])}`
                        : days.map(dayLabel).join(', ');
                return `${daysLabel} ${hours.map((hour) => `${hour.start}-${hour.end}`).join(', ')}`;
            })
            .join('\n');
    };

    const renderRank = (rank: Rank): JSX.Element => {
        const dispatchGroupNames = getDispatchGroupNames(rank, dispatchGroups);

        return (
            <div key={rank.id} className={classes.rank}>
                <div className={classes.rankHeader}>
                    <b className={classes.name}>{rank.name}</b>
                    {rank.status && (
                        <Chip
                            size="small"
                            label={t(`status.${rank.status}`, { defaultValue: rank.status })}
                            className={clsx(classes.chip, getStatusClass(rank.status, classes))}
                        />
                    )}
                    {rank.category && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={t(`category.${rank.category}`, { defaultValue: rank.category })}
                            className={classes.chipCategory}
                        />
                    )}
                    <span style={{ flex: 1 }} />
                    <TooltipButton title={t('edit_button')}>
                        <Button
                            size="small"
                            style={{ marginLeft: 8 }}
                            variant="contained"
                            color="secondary"
                            component={RouterLink}
                            to={paths.ranksEdit.replace(':id', rank.id)}
                            className={globalClasses.btnAutoWidth}
                        >
                            <Icon fontSize="small">edit</Icon>
                        </Button>
                    </TooltipButton>
                </div>
                <div className={classes.details}>
                    {renderDetail(
                        t('dispatch_group'),
                        dispatchGroupNames.length > 0 ? dispatchGroupNames.sort().join(', ') : EMPTY_VALUE,
                    )}
                    {renderDetail(t('capacity'), rank.capacity != null ? String(rank.capacity) : EMPTY_VALUE)}
                    {renderDetail(
                        t('common:form.start_date'),
                        rank.start_datetime ? DateUtils.stringToDate(rank.start_datetime, false) : EMPTY_VALUE,
                    )}
                    {renderDetail(
                        t('common:form.end_date'),
                        rank.end_datetime ? DateUtils.stringToDate(rank.end_datetime, false) : t('no_time_limit'),
                    )}
                    {renderDetail(t('opening_hours'), renderOpeningHours(rank.opening_hours))}
                </div>
            </div>
        );
    };

    return (
        <App
            needRights={AccountRight.DRIVER_PREBOOK}
            loading={loadingRanks}
            header={{
                title: t('title'),
                button: (
                    <ButtonTo
                        rights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}
                        to={paths.ranksAdd}
                        startIcon={<Icon>add</Icon>}
                        text={t('common:add')}
                        variante="rectangle"
                    />
                ),
            }}
        >
            <ErrorMessage payload={rankError} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flex: 1 }}>
                    <div className={globalClasses.block} style={{ flex: 1, marginRight: 4, padding: '20px 28px' }}>
                        {inactiveCount > 0 && (
                            <div className={classes.toolbar}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setShowInactive((visible) => !visible)}
                                    startIcon={
                                        <Icon fontSize="small">{showInactive ? 'visibility_off' : 'visibility'}</Icon>
                                    }
                                >
                                    {showInactive ? t('hide_inactive') : t('show_inactive', { nb: inactiveCount })}
                                </Button>
                            </div>
                        )}
                        <Listing datas={visibleRanks.map(renderRank)} />
                    </div>
                </div>
            </div>
        </App>
    );
};

const RanksListPageWithStyleAndTranslation = withGlobalStyles(withTranslation(['rank', 'common'])(RanksListPage));

export default RanksListPageWithStyleAndTranslation;
